// hooks/useRazorpay.js - FIXED FOR GUEST CHECKOUT
import { useState } from "react";
import { ordersAPI } from "../services/api";
import toast from "react-hot-toast";

export const useRazorpay = () => {
  const [loading, setLoading] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        console.log("✅ Razorpay script already loaded");
        resolve(true);
        return;
      }

      console.log("📦 Loading Razorpay script...");
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        console.log("✅ Razorpay script loaded successfully");
        resolve(true);
      };
      script.onerror = () => {
        console.error("❌ Failed to load Razorpay script");
        toast.error("Failed to load Razorpay SDK");
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const processPayment = async (orderData, onSuccess, onFailure) => {
    console.log("🚀 Starting payment process...");
    console.log("📦 Order data:", orderData);

    try {
      setLoading(true);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment gateway");
      }

      // Calculate order totals
      const subtotal = orderData.items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const shipping = orderData.shipping || 0;
      const tax = orderData.tax || subtotal * 0.18;
      const discount = orderData.discount || 0;
      const total = subtotal + shipping + tax - discount;

      console.log("💵 Order totals:", {
        subtotal,
        shipping,
        tax,
        discount,
        total,
      });

      // Check if user is authenticated
      const token = localStorage.getItem("token");
      const isGuest = !token;

      console.log(`👤 User Type: ${isGuest ? "GUEST" : "AUTHENTICATED"}`);

      // ✅ FIX: Extract guest info properly
      const guestEmail = isGuest
        ? orderData.email || orderData.shippingAddress?.email
        : null;

      const guestFirstName = isGuest
        ? orderData.firstName || orderData.shippingAddress?.firstName || "Guest"
        : null;

      const guestLastName = isGuest
        ? orderData.lastName || orderData.shippingAddress?.lastName || "User"
        : null;

      console.log("📧 Guest Info:", {
        email: guestEmail,
        firstName: guestFirstName,
        lastName: guestLastName,
      });

      // Step 1: Create Razorpay order via backend
      console.log("📤 Creating payment order on backend...");
      const paymentOrderResponse = await ordersAPI.createPaymentOrder({
        amount: total,
        currency: "INR",
        receipt: `order_${Date.now()}`,
        notes: {
          isGuest: isGuest,
          firstName: guestFirstName,
          lastName: guestLastName,
          email: guestEmail,
          phone: orderData.phone || orderData.shippingAddress?.phone,
          customerName: `${guestFirstName} ${guestLastName}`,
          customerEmail: guestEmail,
          items: orderData.items?.length || 0,
          shipping: orderData.shippingAddress?.city || "Unknown",
        },
      });

      console.log("📥 Payment order response:", paymentOrderResponse.data);

      if (!paymentOrderResponse.data.success) {
        throw new Error(
          paymentOrderResponse.data.message || "Failed to create payment order"
        );
      }

      const { order, key, user } = paymentOrderResponse.data;

      console.log("✅ Razorpay order created successfully!");
      console.log("   Order ID:", order.id);
      console.log("   Amount:", order.amount, "paise");
      console.log("   Key ID:", key);
      console.log("   Is Guest:", isGuest);

      // Step 2: Configure Razorpay checkout options
      const options = {
        key: key,
        amount: order.amount,
        currency: order.currency,
        name: "LILYTH",
        description: "Fashion Purchase",
        image: "/logo192.png",
        order_id: order.id,
        handler: async function (response) {
          console.log("💳 Payment successful!", response);

          // Show loader immediately
          if (window.__showProcessingScreen) {
            console.log("⚡ Showing processing screen immediately");
            window.__showProcessingScreen();
          }

          try {
            console.log("🔐 Verifying payment on backend...");

            // ✅ CRITICAL FIX: Properly structure orderData for backend
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderData: {
                // ✅ Items (normalized)
                items: orderData.items.map((item) => ({
                  productId: item.productId || item.product || item._id,
                  size: item.size,
                  color: item.color,
                  quantity: item.quantity,
                  price: item.price,
                })),

                // ✅ Guest identification (CRITICAL for guest orders)
                email: guestEmail,
                firstName: guestFirstName,
                lastName: guestLastName,
                phone: orderData.phone || orderData.shippingAddress?.phone,

                // ✅ Addresses
                shippingAddress: {
                  firstName:
                    orderData.shippingAddress?.firstName || guestFirstName,
                  lastName:
                    orderData.shippingAddress?.lastName || guestLastName,
                  addressLine1:
                    orderData.shippingAddress?.addressLine1 ||
                    orderData.shippingAddress?.address,
                  addressLine2: orderData.shippingAddress?.addressLine2,
                  city: orderData.shippingAddress?.city,
                  state: orderData.shippingAddress?.state,
                  postalCode:
                    orderData.shippingAddress?.postalCode ||
                    orderData.shippingAddress?.zipCode,
                  country: orderData.shippingAddress?.country || "India",
                  phone: orderData.shippingAddress?.phone || orderData.phone,
                },

                billingAddress: orderData.billingAddress || {
                  firstName:
                    orderData.shippingAddress?.firstName || guestFirstName,
                  lastName:
                    orderData.shippingAddress?.lastName || guestLastName,
                  addressLine1:
                    orderData.shippingAddress?.addressLine1 ||
                    orderData.shippingAddress?.address,
                  addressLine2: orderData.shippingAddress?.addressLine2,
                  city: orderData.shippingAddress?.city,
                  state: orderData.shippingAddress?.state,
                  postalCode:
                    orderData.shippingAddress?.postalCode ||
                    orderData.shippingAddress?.zipCode,
                  country: orderData.shippingAddress?.country || "India",
                  phone: orderData.shippingAddress?.phone || orderData.phone,
                },

                // ✅ Totals
                subtotal: subtotal,
                shipping: shipping,
                tax: tax,
                discount: discount,
                total: total,

                // ✅ Additional fields
                promoCode: orderData.promoCode || null,
                specialInstructions: orderData.specialInstructions || "",
              },
            };

            console.log("📦 Verification data:", {
              razorpay_order_id: verificationData.razorpay_order_id,
              razorpay_payment_id: verificationData.razorpay_payment_id,
              hasSignature: !!verificationData.razorpay_signature,
              itemCount: verificationData.orderData.items.length,
              email: verificationData.orderData.email,
              total: verificationData.orderData.total,
            });

            const verificationResponse = await ordersAPI.verifyPayment(
              verificationData
            );

            console.log("📥 Verification response:", verificationResponse.data);

            if (verificationResponse.data.success) {
              console.log("✅ Payment verified successfully!");
              toast.success("🎉 Payment successful! Order placed.");
              onSuccess(verificationResponse.data.order);
            } else {
              throw new Error("Payment verification failed");
            }
          } catch (error) {
            console.error("❌ Payment verification error:", error);
            console.error("   Error response:", error.response?.data);

            const errorMessage =
              error.response?.data?.message ||
              error.response?.data?.error ||
              "Payment verification failed. Please contact support.";

            toast.error(errorMessage);
            onFailure(error);
          }
        },
        prefill: {
          name: user?.name || `${guestFirstName} ${guestLastName}`,
          email: user?.email || guestEmail || "",
          contact:
            user?.contact ||
            orderData.phone ||
            orderData.shippingAddress?.phone ||
            "",
        },
        notes: {
          address: orderData.shippingAddress?.addressLine1 || "",
          city: orderData.shippingAddress?.city || "",
          state: orderData.shippingAddress?.state || "",
        },
        theme: {
          color: "#b87049",
        },
        modal: {
          ondismiss: function () {
            console.log("⚠️ Payment modal dismissed by user");
            toast("Payment cancelled", {
              icon: "ℹ️",
              duration: 3000,
            });
            setLoading(false);
            onFailure(new Error("Payment cancelled by user"));
          },
        },
      };

      console.log("⚙️ Razorpay options configured");

      // Step 3: Create Razorpay instance
      console.log("🔨 Creating Razorpay instance...");
      if (!window.Razorpay) {
        throw new Error("Razorpay is not loaded");
      }

      const rzp = new window.Razorpay(options);
      console.log("✅ Razorpay instance created");

      // Handle payment failure
      rzp.on("payment.failed", async function (response) {
        console.error("❌ Payment failed:", response.error);

        try {
          await ordersAPI.handlePaymentFailure({
            razorpay_order_id: order.id,
            error_code: response.error.code,
            error_description: response.error.description,
            error_source: response.error.source,
            error_step: response.error.step,
            error_reason: response.error.reason,
          });
        } catch (error) {
          console.error("Error logging payment failure:", error);
        }

        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
        onFailure(response.error);
      });

      // Step 4: Open Razorpay modal
      console.log("🚪 Opening Razorpay modal...");
      rzp.open();
      console.log("✅ Razorpay modal opened successfully!");
    } catch (error) {
      console.error("❌ Payment process error:", error);
      console.error("   Error message:", error.message);
      console.error("   Error response:", error.response?.data);

      toast.error(error.message || "Failed to initiate payment");
      setLoading(false);
      onFailure(error);
    }
  };

  const getTestCardDetails = () => {
    return {
      cards: [
        {
          type: "Visa",
          number: "4111 1111 1111 1111",
          cvv: "Any 3 digits",
          expiry: "Any future date",
        },
        {
          type: "Mastercard",
          number: "5555 5555 5555 4444",
          cvv: "Any 3 digits",
          expiry: "Any future date",
        },
      ],
      upi: "success@razorpay",
      netbanking: "All banks supported in test mode",
    };
  };

  return {
    processPayment,
    loading,
    getTestCardDetails,
  };
};

export default useRazorpay;
